import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useComposerToolsQuery } from "@/features/playground/hooks/useComposerQueries";
import { BookOpenCheck, FolderOpen, Globe, Layers } from "lucide-react";
import { Tooltip } from "@/components/ui/tooltip-card";

import { AgentToolItem } from "./agent-tools-item";
import { useState } from "react";
import { cn } from "@/utils/cn";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

export default function AgentToolsList({
  onOpen,
  open = false,
  id,
  disabled = false,
}: {
  onOpen?: (rect: DOMRect, trigger?: HTMLButtonElement) => void;
  open?: boolean;
  id?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [openChange, setOpenChange] = useState(false);
  const toolsList = [
    {
      name: t("playground.listCollections"),
      icon: <Layers className="w-3 h-3" />,
    },
    {
      name: t("playground.retrieveFromCollection"),
      icon: <BookOpenCheck className="w-3 h-3" />,
    },
    {
      name: t("playground.webSearch"),
      icon: <Globe className="w-3 h-3" />,
    },
  ];
  const { data, isLoading } = useComposerToolsQuery();

  if (onOpen) {
    return (
      <Tooltip content={t("playground.availableTools")}>
        <Button
          size="icon"
          type="button"
          variant="ghost"
          aria-label={t("playground.availableTools")}
          aria-expanded={open}
          aria-controls={open ? id : undefined}
          aria-haspopup="dialog"
          disabled={disabled}
          onClick={(e) =>
            onOpen(e.currentTarget.getBoundingClientRect(), e.currentTarget)
          }
          className={cn(
            "size-10 transition-all p-2 cursor-pointer duration-150 rounded-[35px] hover:bg-secondary/50",
          )}
        >
          <FolderOpen className="size-6 text-foreground" />
        </Button>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu defaultOpen={openChange} onOpenChange={setOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          type="button"
          variant="ghost"
          aria-label={t("playground.availableTools")}
          disabled={disabled}
          className={cn(
            "size-10 transition-all p-2 cursor-pointer duration-150 rounded-[35px] ",
            {
              "bg-secondary text-[var(--color-100)] transition-all duration-200":
                openChange,
            },
          )}
        >
          <FolderOpen className="size-6 text-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "bg-[var(--card-background)] border-[1px] border-solid border-ring",
          {
            "flex justify-center h-12 items-center": isLoading,
          },
        )}
      >
        {isLoading ? (
          <Spinner className="motion-reduce:animate-none" />
        ) : (
          data?.map((tool, index) => (
            <AgentToolItem
              key={tool.id}
              icon={toolsList[index]?.icon ?? <FolderOpen className="size-3" />}
              id={tool.id}
              label={toolsList[index]?.name ?? tool.name}
            />
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
